import {
  Icon,
  IconButton,
  type IconButtonProps,
  Tooltip,
  type TooltipProps,
} from '@chakra-ui/react';
import type { CentralIconBaseProps } from 'central-icons/CentralIconBase';
import type React from 'react';
import { forwardRef } from 'react';

import { FeatureFlag } from '@revelio/core';

import { useUnleashFlag } from '../../../../shared/unleash-flags/use-unleash-flag';

interface ActionBarIconButtonProps
  extends Omit<IconButtonProps, 'icon' | 'size' | 'variant'> {
  icon: React.FC<CentralIconBaseProps>;
  iconSize?: number;
  tooltipLabel?: string;
  tooltipProps?: Omit<TooltipProps, 'label' | 'children'>;
}

export const ActionBarIconButton = forwardRef<
  HTMLButtonElement,
  ActionBarIconButtonProps
>(
  (
    {
      icon,
      iconSize = 16,
      'aria-label': ariaLabel,
      tooltipLabel,
      tooltipProps,
      ...props
    },
    ref
  ) => {
    const isNewDashboardLayout = useUnleashFlag(FeatureFlag.DashboardNewLayout);
    const button = (
      <IconButton
        ref={ref}
        variant={isNewDashboardLayout ? 'flat' : 'outline'}
        colorScheme="gray"
        rounded="full"
        icon={<Icon as={icon} size={iconSize} />}
        aria-label={(ariaLabel || tooltipLabel) ?? ''}
        {...props}
      />
    );

    if (tooltipLabel) {
      return (
        <Tooltip
          label={tooltipLabel}
          hasArrow
          animation="none"
          openDelay={300}
          variant="label"
          {...tooltipProps}
        >
          {button}
        </Tooltip>
      );
    }

    return button;
  }
);

ActionBarIconButton.displayName = 'ActionBarIconButton';
